import { useState } from "react";
import { ArrowDown, ArrowUp, Check, Loader2, RefreshCw, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import {
  MAX_ROTATOR_GROUPS,
  filterGroups,
  isNearlyFull,
  moveItem,
  rotatorErrorMessage,
  selectAll,
  toggleSelection,
  type AdminGroupOption,
} from "@/lib/rotators";
import { useAdminGroups, useGroupRefreshJob, useRequestGroupRefresh, useSaveRotator, type Rotator } from "@/lib/hooks/useRotators";

interface RotatorWizardProps {
  readonly initial: Rotator | null;
  readonly onClose: () => void;
}

function RefreshStatus(): JSX.Element | null {
  const { data: job } = useGroupRefreshJob();
  if (!job) return null;
  if (job.status === "pending" || job.status === "running") {
    return (
      <span role="status" className="flex items-center gap-1 text-xs text-muted-foreground">
        <Loader2 size={12} className="animate-spin" /> Buscando seus grupos no WhatsApp...
      </span>
    );
  }
  if (job.status === "failed") {
    return <span role="alert" className="text-xs text-[hsl(var(--destructive))]">{rotatorErrorMessage(job.error ?? "")}</span>;
  }
  return null;
}

function GroupStep({ selected, onChange }: { readonly selected: readonly string[]; readonly onChange: (next: readonly string[]) => void }): JSX.Element {
  const [query, setQuery] = useState("");
  const { data: groups = [], isLoading } = useAdminGroups();
  const refresh = useRequestGroupRefresh();
  const visible = filterGroups(groups, query);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">Só aparecem grupos em que o seu número conectado é administrador.</p>
        <Button type="button" variant="secondary" size="sm" onClick={() => refresh.mutate()} disabled={refresh.isPending}>
          <RefreshCw size={14} className={cn(refresh.isPending && "animate-spin")} /> Atualizar grupos
        </Button>
      </div>
      <RefreshStatus />
      {refresh.error && <p role="alert" className="text-xs text-[hsl(var(--destructive))]">{refresh.error.message}</p>}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[12rem]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input aria-label="Buscar grupo" placeholder="Buscar grupo" value={query} onChange={(e) => setQuery(e.target.value)} className="pl-9" />
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={() => onChange(selectAll(selected, visible.map((g) => g.jid)))} disabled={visible.length === 0}>
          Selecionar todos
        </Button>
        <span className="text-xs font-mono tabular-nums text-muted-foreground" aria-live="polite">
          {selected.length}/{MAX_ROTATOR_GROUPS}
        </span>
      </div>
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Carregando grupos...</p>
      ) : groups.length === 0 ? (
        <p className="rounded-lg border border-dashed border-[hsl(var(--border-strong))] p-4 text-center text-sm text-muted-foreground">
          Nenhum grupo encontrado. Toque em Atualizar grupos para buscar no WhatsApp.
        </p>
      ) : (
        <ul className="flex max-h-80 flex-col gap-1 overflow-y-auto" aria-label="Grupos disponíveis">
          {visible.map((g) => (
            <GroupOption key={g.jid} group={g} checked={selected.includes(g.jid)} disabled={!selected.includes(g.jid) && selected.length >= MAX_ROTATOR_GROUPS} onToggle={() => onChange(toggleSelection(selected, g.jid))} />
          ))}
        </ul>
      )}
    </div>
  );
}

function GroupOption({ group, checked, disabled, onToggle }: { readonly group: AdminGroupOption; readonly checked: boolean; readonly disabled: boolean; readonly onToggle: () => void }): JSX.Element {
  return (
    <li>
      <label className={cn("flex items-center gap-3 rounded-lg border px-3 py-2 text-sm", checked ? "border-[hsl(var(--primary)/0.6)] bg-[hsl(var(--primary)/0.06)]" : "border-[hsl(var(--border))]", disabled ? "opacity-50" : "cursor-pointer")}>
        <input type="checkbox" checked={checked} disabled={disabled} onChange={onToggle} className="h-4 w-4 accent-[hsl(var(--primary))]" />
        <span className="flex-1 truncate text-foreground">{group.title ?? group.jid}</span>
        {group.size !== null && (
          <span className={cn("text-xs font-mono tabular-nums", isNearlyFull(group.size) ? "text-[hsl(var(--gold))]" : "text-muted-foreground")}>
            {group.size} membros{isNearlyFull(group.size) ? " · quase cheio" : ""}
          </span>
        )}
      </label>
    </li>
  );
}

function OrderStep({ name, onName, order, onOrder, titles }: { readonly name: string; readonly onName: (v: string) => void; readonly order: readonly string[]; readonly onOrder: (v: readonly string[]) => void; readonly titles: ReadonlyMap<string, string> }): JSX.Element {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="rotator-name">Nome do rodízio</Label>
        <Input id="rotator-name" maxLength={60} value={name} onChange={(e) => onName(e.target.value)} placeholder="Ex.: Grupos de ofertas" />
      </div>
      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-foreground">Ordem de distribuição</span>
        <p className="text-xs text-muted-foreground">Cada clique vai para o próximo grupo da lista e depois volta ao primeiro.</p>
        <ol className="flex flex-col gap-1">
          {order.map((jid, i) => (
            <li key={jid} className="flex items-center gap-2 rounded-lg border border-[hsl(var(--border))] px-3 py-2 text-sm">
              <span className="w-5 font-mono text-xs text-muted-foreground">{i + 1}.</span>
              <span className="flex-1 truncate text-foreground">{titles.get(jid) ?? jid}</span>
              <Button type="button" variant="ghost" size="sm" aria-label={`Subir ${titles.get(jid) ?? jid}`} disabled={i === 0} onClick={() => onOrder(moveItem(order, i, i - 1))}>
                <ArrowUp size={14} />
              </Button>
              <Button type="button" variant="ghost" size="sm" aria-label={`Descer ${titles.get(jid) ?? jid}`} disabled={i === order.length - 1} onClick={() => onOrder(moveItem(order, i, i + 1))}>
                <ArrowDown size={14} />
              </Button>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

export function RotatorWizard({ initial, onClose }: RotatorWizardProps): JSX.Element {
  const [step, setStep] = useState<1 | 2>(1);
  const [selected, setSelected] = useState<readonly string[]>(initial?.group_rotator_groups.map((g) => g.group_jid) ?? []);
  const [name, setName] = useState(initial?.name ?? "");
  const { data: groups = [] } = useAdminGroups();
  const save = useSaveRotator();
  const titles = new Map<string, string>([
    ...(initial?.group_rotator_groups.map((g) => [g.group_jid, g.title ?? g.group_jid] as const) ?? []),
    ...groups.map((g) => [g.jid, g.title ?? g.jid] as const),
  ]);

  async function submit(): Promise<void> {
    await save.mutateAsync({ id: initial?.id ?? null, name: name.trim(), jids: selected });
    onClose();
  }

  return (
    <Card variant="overlay" className="flex flex-col gap-5 p-5">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-foreground">{initial ? "Editar rodízio" : "Novo rodízio"}</h2>
        <ol className="flex items-center gap-2 text-xs" aria-label="Etapas">
          {(["Grupos", "Nome e ordem"] as const).map((label, i) => (
            <li key={label} aria-current={step === i + 1 ? "step" : undefined} className={cn("flex items-center gap-1 rounded-full px-2 py-0.5", step === i + 1 ? "bg-[hsl(var(--primary)/0.15)] text-[hsl(var(--primary))]" : "text-muted-foreground")}>
              {step > i + 1 ? <Check size={12} /> : <span>{i + 1}</span>} {label}
            </li>
          ))}
        </ol>
      </div>
      {step === 1 ? (
        <GroupStep selected={selected} onChange={setSelected} />
      ) : (
        <OrderStep name={name} onName={setName} order={selected} onOrder={setSelected} titles={titles} />
      )}
      {save.error && <p role="alert" className="text-sm text-[hsl(var(--destructive))]">{save.error.message}</p>}
      <div className="flex justify-between gap-2">
        <Button type="button" variant="ghost" onClick={step === 1 ? onClose : () => setStep(1)}>
          {step === 1 ? "Cancelar" : "Voltar"}
        </Button>
        {step === 1 ? (
          <Button type="button" onClick={() => setStep(2)} disabled={selected.length === 0}>
            Continuar
          </Button>
        ) : (
          <Button type="button" onClick={() => void submit().catch(() => undefined /* erro exibido via save.error */)} disabled={save.isPending || name.trim().length === 0}>
            {save.isPending ? "Salvando..." : initial ? "Salvar alterações" : "Criar rodízio"}
          </Button>
        )}
      </div>
    </Card>
  );
}
